import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types/product';
import { BestOfCategory } from '@/contracts/bestOf';

/**
 * Data facade for Best-Of category pages
 * Reuses getBrowseProducts logic with category filter + Circle Score sorting
 */
export async function getBestOfProducts(
  category: BestOfCategory,
  userId?: string
): Promise<Product[]> {
  // Build query with category filter
  let query = supabase
    .from('products')
    .select(`
      id,
      slug,
      title,
      subtitle,
      description,
      image,
      profile_image,
      price,
      msrp,
      price_duration,
      rating,
      review_count,
      categories,
      tags,
      service_type,
      is_published,
      is_hot,
      is_select,
      created_at,
      best_for,
      integrations_text,
      circle_score,
      score_popularity,
      score_value,
      score_ease_of_use,
      score_support,
      score_roi,
      score_calculated_at,
      product_plans(price)
    `)
    .eq('is_published', true);

  // Filter by service type if specified
  if (category.serviceType) {
    query = query.eq('service_type', category.serviceType);
  }

  // Filter by category (categories is JSONB array, use @> containment operator)
  query = query.contains('categories', [category.categoryFilter]);

  // Sort by Circle Score DESC (nulls last), then by review_count
  query = query
    .order('circle_score', { ascending: false, nullsFirst: false })
    .order('review_count', { ascending: false })
    .limit(20); // Top 20 products max

  // Execute products + favorites in PARALLEL
  const [productsRes, favoritesRes] = await Promise.all([
    query,
    userId 
      ? supabase
          .from('user_favorites')
          .select('product_id')
          .eq('user_id', userId)
      : Promise.resolve({ data: null, error: null })
  ]);

  if (productsRes.error) throw productsRes.error;

  const favoriteSet = new Set(
    favoritesRes.data?.map(f => f.product_id) || []
  );

  // Transform to Product type
  return (productsRes.data || []).map((product, index) => {
    const integration_tags = product.integrations_text 
      ? product.integrations_text
          .split(/[,;|]/)
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0)
      : [];
    
    const circleScore = product.circle_score ? {
      circle_score: product.circle_score,
      score_popularity: product.score_popularity,
      score_value: product.score_value,
      score_ease_of_use: product.score_ease_of_use,
      score_support: product.score_support,
      score_roi: product.score_roi,
      score_calculated_at: product.score_calculated_at,
    } : null;

    return {
      ...product,
      integration_tags,
      isFavorited: favoriteSet.has(product.slug),
      circleScore,
      rank: index + 1, // Add ranking position
      plansCount: product.product_plans?.length || 0,
      startingPrice: product.product_plans?.length 
        ? Math.min(...product.product_plans.map((p: any) => p.price || 0).filter((p: number) => p > 0))
        : product.price,
      gallery: [],
      features: [],
      categories: Array.isArray(product.categories) ? product.categories : [],
      tags: Array.isArray(product.tags) ? product.tags : [],
      best_for: Array.isArray(product.best_for) ? product.best_for : [],
    };
  }) as unknown as Product[];
}
